import React, { useRef } from 'react';
import { ChevronRight, ImagePlus, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '@/lib/utils';
import {
  MEMORIAL_KEEPSAKE_SQUARE_GIFT,
  MEMORIAL_PHOTO_LINK_BUILDER_NOTE,
  MEMORIAL_PHOTO_LINK_PREVIEW_GUIDANCE,
} from '../../content/memorialKeepsakesContent';
import { KEEPSAKE_FRAME_STYLES, KEEPSAKE_LAYOUTS, getOrientationLabel } from '../../utils/memorialPhotoLinkBuilder';
import { KeepsakeFrameMockup, OrientationToggle } from './KeepsakeFrameMockup';
import { KeepsakeImagePositionEditor } from './KeepsakeImagePositionEditor';
import { CUSTOMER_IMAGE_ACCEPT, validateCustomerImageFile } from '../../utils/imageUploadValidation';

const STEPS = [
  { id: 1, label: 'Size' },
  { id: 2, label: 'Image' },
  { id: 3, label: 'Position' },
  { id: 4, label: 'Frame' },
  { id: 5, label: 'Layout' },
  { id: 6, label: 'Preview' },
];

function StepChip({ step, current, label, onClick }) {
  const active = step === current;
  const done = step < current;
  return (
    <button
      type="button"
      onClick={() => onClick(step)}
      className={cn(
        'rounded-full border px-2.5 py-1 text-[10px] font-medium uppercase tracking-wide transition',
        active && 'border-[hsl(152_32%_20%)] bg-[hsl(152_32%_20%)] text-[hsl(42_30%_96%)]',
        done && !active && 'border-[hsl(38_14%_86%)] bg-white text-[#5c534a]',
        !active && !done && 'border-[hsl(38_14%_88%)] bg-[hsl(42_28%_98%)] text-muted-foreground',
      )}
    >
      {label}
    </button>
  );
}

function ProductSizeButton({ product, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(product.id)}
      className={cn(
        'rounded-xl border p-3 text-left transition hover:border-[#8b7355]/40',
        selected
          ? 'border-[hsl(152_32%_20%)] bg-[hsl(152_32%_20%_/_0.06)]'
          : 'border-[hsl(38_14%_86%)] bg-white/80',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-medium text-sm text-[#1c1917]">{product.title}</p>
        {product.recommended ? (
          <Badge className="rounded-full bg-[hsl(152_32%_20%)] text-[10px] text-[hsl(42_30%_96%)]">Recommended</Badge>
        ) : null}
        {product.largest ? (
          <Badge variant="outline" className="rounded-full text-[10px]">
            Largest available size
          </Badge>
        ) : null}
      </div>
      <p className="mt-1 text-[11px] text-[#5c534a]">{product.size}</p>
      {product.sizeDetail ? <p className="mt-0.5 text-[10px] text-muted-foreground">{product.sizeDetail}</p> : null}
    </button>
  );
}

export function MemorialPhotoLinkBuilder({
  published,
  builderState,
  product,
  frameStyle,
  layout,
  previewImageUrl,
  personPortraitUrl,
  imageStatus,
  orientation,
  imagePosition,
  imageEdits,
  mainProducts,
  squareGift,
  name,
  dates,
  memorialTitle,
  onProductChange,
  onFrameChange,
  onLayoutChange,
  onStepChange,
  onOrientationChange,
  onImagePositionChange,
  onResetImagePosition,
  onImageEditsChange,
  onResetImageEdits,
  onRotateImageLeft,
  onRotateImageRight,
  onFitImage,
  onFillImage,
  onUsePortrait,
  onChooseLater,
  onUploadFile,
  canUploadKeepsake = false,
  uploadingKeepsake = false,
  onOpenPreview,
  onOpenCheckoutReview,
}) {
  const fileRef = useRef(null);
  const step = builderState.step || 1;

  return (
    <div
      className="mt-6 rounded-2xl border border-[hsl(38_14%_86%)]/80 bg-gradient-to-br from-white/90 to-[hsl(42_28%_98%)] p-5 sm:p-6"
      data-testid="memorial-photo-link-builder"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8b7355]">Product builder</p>
          <h4 className="mt-1 font-serif text-lg font-light text-[#1c1917]">Design your Memorial Photo Link</h4>
          <p className="mt-2 max-w-xl text-sm text-[#5c534a]">{MEMORIAL_PHOTO_LINK_PREVIEW_GUIDANCE}</p>
          <div className="mt-3">
            <OrientationToggle orientation={orientation} onChange={onOrientationChange} />
          </div>
        </div>
        <KeepsakeFrameMockup
          imageUrl={previewImageUrl}
          name={name}
          dates={dates}
          memorialTitle={memorialTitle}
          frameStyle={frameStyle}
          layout={layout}
          product={product}
          orientation={orientation}
          imagePosition={imagePosition}
          imageEdits={imageEdits}
          published={published}
          size="sm"
        />
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {STEPS.map((s) => (
          <StepChip key={s.id} step={s.id} current={step} label={s.label} onClick={onStepChange} />
        ))}
      </div>

      <div className="mt-5 min-h-[120px]">
        {step === 1 ? (
          <div className="space-y-4">
            <div className="grid gap-2 sm:grid-cols-3">
              {(mainProducts || []).map((p) => (
                <ProductSizeButton
                  key={p.id}
                  product={p}
                  selected={builderState.productId === p.id}
                  onSelect={(id) => {
                    onProductChange(id);
                    onStepChange(2);
                  }}
                />
              ))}
            </div>
            {squareGift ? (
              <div className="rounded-xl border border-dashed border-[hsl(38_14%_86%)] bg-[hsl(42_28%_98%)]/80 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Optional</p>
                <ProductSizeButton
                  product={squareGift}
                  selected={builderState.productId === squareGift.id}
                  onSelect={(id) => {
                    onProductChange(id);
                    onStepChange(2);
                  }}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {step === 2 ? (
          <div className="space-y-3">
            <p className="text-sm text-[#5c534a]">Choose the photo for your keepsake preview. Orientation is suggested from the image.</p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant={personPortraitUrl ? 'default' : 'outline'}
                className="rounded-full"
                disabled={!personPortraitUrl}
                onClick={onUsePortrait}
              >
                <ImagePlus className="mr-1 h-3.5 w-3.5" />
                Use memorial portrait
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-full"
                disabled={uploadingKeepsake}
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="mr-1 h-3.5 w-3.5" />
                {uploadingKeepsake ? 'Uploading…' : 'Upload keepsake image'}
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept={CUSTOMER_IMAGE_ACCEPT}
                className="hidden"
                disabled={uploadingKeepsake}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const check = validateCustomerImageFile(file);
                    if (!check.ok) {
                      toast.error(check.message);
                      e.target.value = '';
                      return;
                    }
                    onUploadFile(file);
                  }
                  e.target.value = '';
                }}
              />
              <Button type="button" size="sm" variant="ghost" className="rounded-full" onClick={onChooseLater}>
                Choose image later
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">{imageStatus}</p>
            <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => onStepChange(3)}>
              Continue <ChevronRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
        ) : null}

        {step === 3 ? (
          <>
            <KeepsakeImagePositionEditor
              imageUrl={previewImageUrl}
              imagePosition={imagePosition}
              imageEdits={imageEdits}
              orientation={orientation}
              product={product}
              frameStyle={frameStyle}
              layout={layout}
              name={name}
              dates={dates}
              memorialTitle={memorialTitle}
              published={published}
              onChange={onImagePositionChange}
              onReset={onResetImagePosition}
              onFit={onFitImage}
              onFill={onFillImage}
              onEditsChange={onImageEditsChange}
              onResetEdits={onResetImageEdits}
              onRotateLeft={onRotateImageLeft}
              onRotateRight={onRotateImageRight}
            />
            <div className="mt-4">
              <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => onStepChange(4)}>
                Continue <ChevronRight className="ml-1 h-3 w-3" />
              </Button>
            </div>
          </>
        ) : null}

        {step === 4 ? (
          <div className="grid gap-2 sm:grid-cols-2">
            {KEEPSAKE_FRAME_STYLES.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => {
                  onFrameChange(f.id);
                  onStepChange(5);
                }}
                className={cn(
                  'rounded-xl border p-3 text-left text-sm',
                  builderState.frameStyleId === f.id
                    ? 'border-[hsl(152_32%_20%)] bg-[hsl(152_32%_20%_/_0.06)]'
                    : 'border-[hsl(38_14%_86%)] bg-white/80',
                )}
              >
                <span className={cn('mb-2 inline-block h-3 w-8 rounded-sm border', f.ring)} aria-hidden />
                {f.label}
              </button>
            ))}
          </div>
        ) : null}

        {step === 5 ? (
          <div className="grid gap-2 sm:grid-cols-2">
            {KEEPSAKE_LAYOUTS.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => {
                  onLayoutChange(l.id);
                  onStepChange(6);
                }}
                className={cn(
                  'rounded-xl border p-3 text-left text-sm text-[#5c534a]',
                  builderState.layoutId === l.id
                    ? 'border-[hsl(152_32%_20%)] bg-[hsl(152_32%_20%_/_0.06)]'
                    : 'border-[hsl(38_14%_86%)] bg-white/80',
                )}
              >
                {l.label}
              </button>
            ))}
          </div>
        ) : null}

        {step === 6 ? (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-[#5c534a]">
              <p>
                <span className="font-medium text-[#1c1917]">{product?.title}</span> · {getOrientationLabel(orientation)} ·{' '}
                {frameStyle?.label} · {layout?.label}
              </p>
              <p className="mt-1 text-xs">{imageStatus}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" className="rounded-full" onClick={onOpenPreview}>
                Open full preview
              </Button>
              <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={onOpenCheckoutReview}>
                Review keepsake
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <p className="mt-4 text-[11px] leading-relaxed text-muted-foreground">{MEMORIAL_PHOTO_LINK_BUILDER_NOTE}</p>
    </div>
  );
}

export default MemorialPhotoLinkBuilder;
